import { useMsal } from '@azure/msal-react'
import {
  ActionButton,
  ActionMenuItem,
  Banner,
  ControlledPhoneField,
  ControlledSelect,
  ControlledTextField,
  FieldGrid,
  SubsectionHeader,
  useCan,
  useFormDialog,
  useIfUser,
} from '@if/mui'

import { Box } from '@mui/material'
import { access } from '@src/access'
import { isFederatedUser, passwordResetRequest } from '@src/authConfig'
import HideUnreadMessagePopupsCheckbox from '@src/components/HideUnreadMessagePopupsCheckbox'
import { useCanOptOutOfUnreadMessagePopups } from '@src/hooks/unreadMessagePopup-hooks'
import PageSpinner from '@src/components/PageSpinner'
import { useMerchantById } from '@src/data/api/merchants-api/merchants-api'
import { User, useGetUserProfile, useUpdateUserProfile } from '@src/data/api/user-api/user-api'
import { FinancingCompanyConfigs } from '@src/types/Constants'
import { EditProfileInformation, EditProfileInformationSchema } from '@src/types/profileSchema'
import { DefaultValues } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

/**
 * The user's "Edit profile" dialog, wired through the DS `useFormDialog`. Opened
 * imperatively from the app-bar profile menu via the returned handle's `edit()`.
 * Iceberg employees (federated MSAL accounts) enter via the B2C IdP federation
 * policy and their profile is managed in Entra ID Iceberg, not B2C — so for them
 * the form is read-only: disabled fields, no submit, no password-reset action.
 */
export const useEditProfileDialog = () => {
  const { t, i18n } = useTranslation()
  const { instance, accounts } = useMsal()
  const userToken = useIfUser()
  const isInternalUser = isFederatedUser(accounts[0])
  const [user, isLoadingUser] = useGetUserProfile(userToken?.id, isInternalUser)
  const [merchant] = useMerchantById(userToken?.merchantId ?? '')
  const [updateUser, , , resetUpdateUser] = useUpdateUserProfile()
  const canOptOut = useCanOptOutOfUnreadMessagePopups()
  const canMessage = useCan(access.messaging)
  // The pop-up only ever shows where instant messaging does, so the setting follows
  // it — behind its own feature switch, which also gates the checkbox itself.
  const showNotifications = canOptOut && canMessage && !userToken?.isBannerUser

  const handlePasswordReset = async () => {
    await instance.loginRedirect(passwordResetRequest)
  }

  return useFormDialog<EditProfileInformation, void, User>({
    title: t('profile.title'),
    maxWidth: 'md',
    schema: EditProfileInformationSchema,
    defaultValues: () => (user ?? {}) as DefaultValues<EditProfileInformation>,
    // The old dialog closed silently on save; keep that (no success toast).
    successMessage: false,
    // Read-only for internal users: hide the submit; the password-reset action below
    // is also withheld.
    hideConfirm: isInternalUser,
    confirmLabel: t('profile.updateInformation'),
    confirmAdjacentActions: isInternalUser
      ? undefined
      : ({ submitting }) => (
          <ActionButton emphasis="principal" onClick={handlePasswordReset} disabled={submitting}>
            {t('profile.changePassword')}
          </ActionButton>
        ),
    content: ({ form }) => (
      <>
        <PageSpinner isLoading={isLoadingUser} />
        {isInternalUser && (
          <Banner severity="info" sx={{ mb: 2 }}>
            <Trans
              i18nKey="profile.viewingAsInternalUser"
              values={{
                merchantName: merchant?.name ?? '',
                companyName: merchant?.financingCompanyId
                  ? FinancingCompanyConfigs[merchant.financingCompanyId].name
                  : '',
              }}
              components={{ strong: <strong /> }}
            />
          </Banner>
        )}
        <Box sx={{ mt: 1 }}>
          <FieldGrid columns={{ xs: 1, md: 2 }}>
            <ControlledSelect
              control={form.control}
              name="language"
              fullWidth
              disableEmptyOption
              label={t('common.language')}
              defaultValue="en"
              disabled={isInternalUser}
              span="full"
            >
              <ActionMenuItem value="en">{t('common.english')}</ActionMenuItem>
              <ActionMenuItem value="fr">{t('common.french')}</ActionMenuItem>
            </ControlledSelect>
            <ControlledTextField
              id="firstName"
              name="firstName"
              control={form.control}
              label={t('common.firstName')}
              slotProps={{ htmlInput: { maxLength: 50 } }}
              required
              fullWidth
              disabled={isInternalUser}
            />
            <ControlledTextField
              id="lastName"
              name="lastName"
              control={form.control}
              label={t('common.lastName')}
              slotProps={{ htmlInput: { maxLength: 50 } }}
              required
              fullWidth
              disabled={isInternalUser}
            />
            <ControlledTextField
              id="jobTitle"
              name="jobTitle"
              control={form.control}
              label={t('common.jobTitle')}
              slotProps={{ htmlInput: { maxLength: 100 } }}
              required
              fullWidth
              disabled={isInternalUser}
            />
            <ControlledPhoneField
              control={form.control}
              name="phone"
              label={t('common.phone')}
              fullWidth
              id="cellPhone"
              disabled={isInternalUser}
            />
          </FieldGrid>
        </Box>
        {showNotifications && (
          <Box sx={{ mt: 3 }}>
            <SubsectionHeader title={t('profile.notifications')} />
            {/* A device setting, not profile data: it applies on change and is deliberately
                not tied to "Update information" — which internal users don't even get. */}
            <HideUnreadMessagePopupsCheckbox />
          </Box>
        )}
      </>
    ),
    onSubmit: async (data) => {
      if (!user) return false
      const cUser = await updateUser({ ...user, ...data, phone: data.phone ?? '' })
      resetUpdateUser()
      if (!cUser) return false
      // Apply the saved language so the dashboard and the app-bar toggle follow.
      await i18n.changeLanguage(data.language)
      return cUser
    },
  })
}
